/**
 * @file services/models.ts
 * @description Service for handling models (fulfillments) data for Model Reviewer/Preparer Dashboard
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { UserFulfillmentRequest } from 'api/models/UserFulfillmentRequest';
import { ApiService } from 'api/services/ApiService';
import { RootStateType } from 'dashboard409a/states/store';

// Query keys for models operations
export const ModelsQueryKeys = {
  MODELS_LIST: 'models-list',
} as const;

/**
 * Hook to get models list for all companies where user is reviewer/preparer
 * @param filterType - Filter by 'current' or 'past' models
 * @param userIds - Optional array of user IDs. If not provided, uses current user ID from Redux store
 * @param page - Optional page number for pagination - Note: Currently not used by backend
 */
export const useModelsList = (
  filterType: 'current' | 'past' = 'current',
  userIds?: number[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  page?: number // Reserved for future pagination support
) => {
  // Get current user ID from Redux store
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  // Create stable string representation of user IDs for dependency comparison
  const userIdsKey = useMemo(() => {
    let ids: number[] = [];
    if (userIds && userIds.length > 0) {
      ids = userIds;
    } else if (currentUserId) {
      ids = [currentUserId];
    }
    return [...ids].sort((a, b) => a - b).join(',');
  }, [userIds, currentUserId]);

  // Memoize targetUserIds to avoid unnecessary recalculations
  const targetUserIds = useMemo(() => {
    if (userIds && userIds.length > 0) {
      return userIds;
    }
    if (currentUserId) {
      return [currentUserId];
    }
    return [];
  }, [userIds, currentUserId]);

  // Create stable query key - sort and join only once, memoized for performance
  const queryKey = useMemo(
    () => [ModelsQueryKeys.MODELS_LIST, filterType, userIdsKey],
    [userIdsKey, filterType]
  );

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (targetUserIds.length === 0) {
        throw new Error('User ID is required');
      }

      // Use the new POST API that accepts multiple user IDs in a single request
      const requestBody: UserFulfillmentRequest = {
        user_ids: targetUserIds,
        status_filter: filterType === 'current' 
          ? UserFulfillmentRequest.status_filter.CURRENT 
          : UserFulfillmentRequest.status_filter.PAST,
      };

      const response = await ApiService.apiDashboardUserModelsCreate(requestBody);

      return response;
    },
    enabled: targetUserIds.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
    placeholderData: previousData => previousData, // Keep previous data while loading new data
  });
};
